import React, { useState, lazy, Suspense } from "react";
import type { WidgetInfo, WidgetType, WidgetSize } from "./widget-declarations";
import { motion } from "framer-motion";
import UserCardWidget from "./UserCardWidget";

// Import a dummy component since the actual ones might not be implemented yet
const DummyWidget = () => (
  <div className="flex items-center justify-center h-full w-full">
    <div className="text-white/70 text-sm">محتوى الويدجت غير متوفر حالياً</div>
  </div>
);

// استيراد مكونات الويدجت المتخصصة بشكل كسول (lazy loading)
// Use a catch for imports that might fail
const lazyImport = (path: string) => {
  return lazy(() =>
    import(/* @vite-ignore */ path).catch(() => ({ default: DummyWidget }))
  );
};

const MailWidget = lazyImport("./MailWidget");
const DriveWidget = lazyImport("./DriveWidget");
const PhotosWidget = lazyImport("./PhotosWidget"); // This is actually now the CoursesWidget
const CalendarWidget = lazyImport("./CalendarWidget");
const ContactsWidget = lazyImport("./ContactsWidget");
const NotesWidget = lazyImport("./NotesWidget");
const NumbersWidget = lazyImport("./NumbersWidget");
const PagesWidget = lazyImport("./PagesWidget");

interface WidgetProps {
  widget: WidgetInfo;
  isEditing?: boolean;
  onSizeChange?: (widgetId: string, newSize: WidgetSize) => void;
  onDragHandleRender?: (handleRef: React.RefObject<HTMLDivElement>) => void;
  onEditClick?: () => void;
  hideHeader?: boolean;
}

const Widget: React.FC<WidgetProps> = ({
  widget,
  isEditing = false,
  onSizeChange,
  onDragHandleRender,
  onEditClick,
  hideHeader = false,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const dragHandleRef = React.useRef<HTMLDivElement>(null);

  // Send the drag handle ref to parent component if needed
  React.useEffect(() => {
    if (onDragHandleRender && dragHandleRef.current) {
      onDragHandleRender(dragHandleRef);
    }
  }, [onDragHandleRender]);

  // دالة لعرض المحتوى الداخلي للويدجت بناءً على نوعه ونقل حجم الويدجت
  const renderWidgetContent = (
    type: WidgetType,
    widgetSize: WidgetSize = "small"
  ) => {
    // مؤشر تحميل لاستخدامه مع Suspense
    const LoadingFallback = () => (
      <div className="flex items-center justify-center w-full h-full">
        <div className="animate-pulse flex flex-col items-center">
          <div className="w-16 h-16 bg-gray-300/30 rounded-full mb-2"></div>
          <div className="h-4 w-28 bg-gray-300/30 rounded"></div>
        </div>
      </div>
    );

    const content = () => {
      switch (type) {
        case "mail":
          return <MailWidget />;
        case "drive":
          return <DriveWidget />;
        case "photos":
          return <PhotosWidget />;
        case "calendar":
          return <CalendarWidget />;
        case "contacts":
          return <ContactsWidget />;
        case "notes":
          return <NotesWidget />;
        case "numbers":
          return <NumbersWidget />;
        case "pages":
          return <PagesWidget />;
        case "user":
          // استخدام مكون UserCardWidget المستورد مباشرة
          return <UserCardWidget size={widgetSize} />;
        default:
          return (
            <div className="text-white/70 text-center p-4">
              محتوى الويدجت غير متوفر
            </div>
          );
      }
    };

    return <Suspense fallback={<LoadingFallback />}>{content()}</Suspense>;
  };

  // تعريف أحجام الويدجت - ضبط الارتفاع المناسب فقط
  const widgetSizeClasses: Record<WidgetSize, string> = {
    small: "h-72",
    medium: "h-96",
    large: "h-112",
    wide: "h-96", // Same height as medium
    full: "h-112", // Same height as large
  };

  return (
    <motion.div
      className={`
        rounded-2xl overflow-hidden shadow-lg relative widget-glass
        ${widgetSizeClasses[widget.size] || "h-64"}
        transition-all duration-300 ease-in-out
        ${isHovered ? "scale-[1.02] shadow-xl" : ""}
        w-full max-w-full
        border border-white/20
      `}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      layout
    >
      {/* Background handled by widget-glass class */}

      {/* رأس الويدجت - عرضه فقط إذا كان hideHeader = false */}
      {!hideHeader && (
        <div className="relative z-10 p-4 flex justify-between items-center border-b border-white/10">
          <div className="flex items-center">
            {/* Drag handle */}
            <div
              ref={dragHandleRef}
              className="w-8 h-8 mr-2 flex items-center justify-center cursor-move opacity-60 hover:opacity-100 transition-opacity"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M4 4H6V6H4V4ZM10 4H12V6H10V4ZM4 10H6V12H4V10ZM10 10H12V12H10V10Z"
                  fill="white"
                />
              </svg>
            </div>
            <div
              className="w-10 h-10 flex items-center justify-center rounded-full mr-3"
              style={{
                backgroundColor: widget.bgColor || "rgba(255, 255, 255, 0.15)",
              }}
            >
              <img src={widget.icon} alt={widget.title} className="w-6 h-6" />
            </div>
            <h3 className="text-white font-medium text-base">{widget.title}</h3>
          </div>

          <div className="flex items-center space-x-2 rtl:space-x-reverse">
            {/* Size Controls - Show only when isEditing or hovered */}
            {(isEditing || isHovered) && onSizeChange && (
              <div className="flex space-x-1 rtl:space-x-reverse mr-3 bg-black/20 p-1 rounded-full">
                <button
                  onClick={() => onSizeChange(widget.id, "small")}
                  className={`w-6 h-6 rounded-full flex items-center justify-center ${
                    widget.size === "small"
                      ? "bg-blue-500 text-white"
                      : "bg-white/10 text-white/70 hover:bg-white/20"
                  }`}
                  title="Small"
                >
                  S
                </button>
                <button
                  onClick={() => onSizeChange(widget.id, "medium")}
                  className={`w-6 h-6 rounded-full flex items-center justify-center ${
                    widget.size === "medium"
                      ? "bg-blue-500 text-white"
                      : "bg-white/10 text-white/70 hover:bg-white/20"
                  }`}
                  title="Medium"
                >
                  M
                </button>
                <button
                  onClick={() => onSizeChange(widget.id, "large")}
                  className={`w-6 h-6 rounded-full flex items-center justify-center ${
                    widget.size === "large"
                      ? "bg-blue-500 text-white"
                      : "bg-white/10 text-white/70 hover:bg-white/20"
                  }`}
                  title="Large"
                >
                  L
                </button>
              </div>
            )}

            {/* Edit button - Show only when hovered and not in editing mode */}
            {onEditClick && !isEditing && isHovered && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onEditClick();
                }}
                className="p-2 mr-3 text-blue-300 rounded-full hover:bg-white/10 transition-colors"
                title="إعدادات"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            )}

            {/* أزرار التحكم على طراز أبل */}
            <div className="flex space-x-2 rtl:space-x-reverse">
              <div className="w-4 h-4 rounded-full bg-red-500 hover:bg-red-600 transition-colors" />
              <div className="w-4 h-4 rounded-full bg-yellow-500 hover:bg-yellow-600 transition-colors" />
              <div className="w-4 h-4 rounded-full bg-green-500 hover:bg-green-600 transition-colors" />
            </div>
          </div>
        </div>
      )}

      {/* محتوى الويدجت */}
      <div
        className={`relative z-10 overflow-auto scrollbar-hide ${
          hideHeader ? "p-0 h-full" : "p-6"
        }`}
        style={hideHeader ? undefined : { height: "calc(100% - 60px)" }}
      >
        {widget.type === "user" && hideHeader ? (
          <div className="h-full">
            {renderWidgetContent(widget.type, widget.size)}
          </div>
        ) : (
          renderWidgetContent(widget.type, widget.size)
        )}
      </div>

      {/* زر التفاعل يظهر عند التحويم على طراز أبل */}
      {!isEditing && !hideHeader && (
        <div
          className={`
            absolute bottom-4 left-0 right-0 flex justify-center z-20
            transition-opacity duration-300
            ${isHovered ? "opacity-100" : "opacity-0"}
          `}
        ></div>
      )}
    </motion.div>
  );
};

export default Widget;
